import React, { useState, useMemo, useEffect } from 'react';
import { useTabCompletion } from '../../../hooks/useTabCompletion';
import { GhostInput } from '../../common/GhostInput';
import { useStore } from '../../../store/useStore';
import type { Resource } from '../../../types';
import { Button } from '../../common/Button';
import { NumberInput } from '../../common/NumberInput';
import { X, Search } from 'lucide-react';

interface ListingModalProps {
    isOpen: boolean;
    onClose: () => void;
    slotId: number;
}

export const ListingModal: React.FC<ListingModalProps> = ({ isOpen, onClose, slotId }) => {
    const market = useStore(state => state.market);
    const resources = useStore(state => state.resources);
    const createListing = useStore(state => state.createListing);
    const lastListingItem = useStore(state => state.lastListingItem);
    const setLastListingItem = useStore(state => state.setLastListingItem);
    const lastListingType = useStore(state => state.lastListingType);
    const setLastListingType = useStore(state => state.setLastListingType);

    const [type, setType] = useState<'buy' | 'sell'>(lastListingType || 'buy');
    const [selectedItem, setSelectedItem] = useState<Resource | null>(null);
    const [amount, setAmount] = useState(1);
    const [price, setPrice] = useState(0);
    const [search, setSearch] = useState('');

    const marketItemNames = useMemo(() =>
        Object.values(market)
            .filter(item => item.id !== 'gold')
            .map(item => item.name),
        [market]
    );

    const { handleKeyDown: handleTabKeyDown, handleInputChange, hint } = useTabCompletion(search, setSearch, marketItemNames, 'includes');

    const items = useMemo(() => {
        const filtered = Object.values(market).filter(item =>
            item.id !== 'gold' && item.name.toLowerCase().includes(search.toLowerCase())
        );
        // Sort by total value (count * market price) descending
        return filtered.sort((a, b) => {
            const valA = (resources[a.id] ?? 0) * a.currentPrice;
            const valB = (resources[b.id] ?? 0) * b.currentPrice;
            return valB - valA;
        });
    }, [market, search, resources]);

    // Initialize with last selected item
    useEffect(() => {
        if (isOpen && !selectedItem && lastListingItem && market[lastListingItem]) {
            handleSelect(lastListingItem);
        }
    }, [isOpen]);

    const handleSelect = (id: Resource) => {
        setSelectedItem(id);
        setLastListingItem(id);
        setPrice(Math.floor(market[id].currentPrice)); // Default to current price

        if (type === 'sell') {
            const stock = resources[id] ?? 0;
            setAmount(stock > 0 ? stock : 1);
        } else {
            setAmount(1);
        }
    };

    const handleSubmit = () => {
        if (!selectedItem) return;
        const success = createListing(slotId, selectedItem, type, amount, price);
        if (success) {
            onClose();
        }
    };

    if (!isOpen) return null;

    const total = amount * price;

    return (
        <div className="modal-overlay">
            <div className="modal-content listing-modal">
                <div className="modal-header">
                    <h2>Create Listing</h2>
                    <button className="close-btn" onClick={onClose}><X size={24} /></button>
                </div>

                <div className="listing-type-toggle">
                    <Button
                        variant={type === 'buy' ? 'primary' : 'secondary'}
                        onClick={() => {
                            setType('buy');
                            setLastListingType('buy');
                            if (selectedItem) setAmount(1);
                        }}
                        fullWidth
                    >
                        Buy Order
                    </Button>
                    <Button
                        variant={type === 'sell' ? 'primary' : 'secondary'}
                        onClick={() => {
                            setType('sell');
                            setLastListingType('sell');
                            if (selectedItem) {
                                const stock = resources[selectedItem] ?? 0;
                                setAmount(stock > 0 ? stock : 1);
                            }
                        }}
                        fullWidth
                    >
                        Sell Order
                    </Button>
                </div>

                <div className="listing-body">
                    <div className="item-selection">
                        <div className="search-bar">
                            <Search size={16} style={{ zIndex: 2, position: 'relative' }} />
                            <GhostInput
                                placeholder="Search items..."
                                value={search}
                                hint={hint}
                                onChange={handleInputChange}
                                onKeyDown={handleTabKeyDown}
                            />
                        </div>
                        <div className="items-list">
                            {items.map(item => (
                                <div
                                    key={item.id}
                                    className={`item-row ${selectedItem === item.id ? 'selected' : ''}`}
                                    onClick={() => handleSelect(item.id)}
                                >
                                    <div className="item-icon">{item.name[0]}</div>
                                    <div className="item-info">
                                        <div className="name">{item.name}</div>
                                        <div className="market-price">Market: {Math.floor(item.currentPrice)} G</div>
                                    </div>
                                    <div className="owned">
                                        Owned: {resources[item.id] ?? 0}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <div className="order-details">
                        {selectedItem && (
                            <>
                                <div className="detail-row">
                                    <label>Quantity</label>
                                    <NumberInput
                                        value={amount}
                                        min={1}
                                        max={type === 'sell' && selectedItem ? (resources[selectedItem] ?? 0) : undefined}
                                        onChange={(val) => {
                                            if (type === 'sell' && selectedItem) {
                                                const stock = resources[selectedItem] ?? 0;
                                                if (val > stock) val = stock;
                                            }
                                            setAmount(val);
                                        }}
                                    />
                                </div>
                                <div className="detail-row">
                                    <label>Price (Each)</label>
                                    <NumberInput
                                        value={price}
                                        min={1}
                                        onChange={(val) => setPrice(Math.floor(val))}
                                    />
                                </div>

                                <div className="order-summary">
                                    <div className="summary-row">
                                        <span>Total: </span>
                                        <span className={total > resources.gold && type === 'buy' ? 'text-danger' : 'text-accent'}>
                                            {total} G
                                        </span>
                                    </div>
                                    {type === 'buy' && (
                                        <div className="wallet-check">
                                            Wallet: {resources.gold} G
                                        </div>
                                    )}
                                </div>

                                <Button
                                    variant="success"
                                    fullWidth
                                    onClick={handleSubmit}
                                    disabled={
                                        amount < 1 || price < 1 ||
                                        (type === 'buy' && total > resources.gold) ||
                                        (type === 'sell' && (resources[selectedItem] ?? 0) < amount)
                                    }
                                >
                                    Confirm {type === 'buy' ? 'Buy' : 'Sell'}
                                </Button>
                            </>
                        )}
                        {!selectedItem && <div className="placeholder-text">Select an item to begin</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};
